import { useSelector } from '@tanstack/react-store';
import type { RowData, Table } from '@/lib/tanstack-table';
import { Checkbox } from '@/components/ui/checkbox';

interface DataTableSelectAllCheckboxProps<TData extends RowData> {
  table: Table<TData>;
  'aria-label': string;
}

/**
 * Page-level "select all" header checkbox (checked / indeterminate / empty).
 *
 * The React Compiler treats `table` as immutable (its identity never changes),
 * so it caches `table.getIsSomePageRowsSelected()` and the checkbox freezes in
 * its first state. `'use no memo'` re-derives on every render, and the store
 * subscription makes it re-render when the selection changes.
 */
export function DataTableSelectAllCheckbox<TData extends RowData>({
  table,
  'aria-label': ariaLabel,
}: DataTableSelectAllCheckboxProps<TData>) {
  'use no memo';
  // Subscribe only; the getters below derive from the same state.
  useSelector(table.store, (state) => state.rowSelection);
  const checked =
    table.getIsAllPageRowsSelected() ||
    (table.getIsSomePageRowsSelected() && 'indeterminate');

  return (
    <Checkbox
      checked={checked}
      onCheckedChange={(value) => table.toggleAllPageRowsSelected(value)}
      aria-label={ariaLabel}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
